import { SnackbarOrigin } from '@mui/material';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import remove from 'lodash/remove';
import uniqBy from 'lodash/uniqBy';
import xorBy from 'lodash/xorBy';

import {
  ProductSearchMaterial,
  SnackbarInfo,
  CommonSliceState,
} from '../../types';
import { isHomePage } from '../../utils/helpers/isPage';

const defaultAnchorOrigin: SnackbarOrigin = {
  horizontal: 'center',
  vertical: 'top',
};

export const initialState: CommonSliceState = {
  cartItems: [],
  createPaletteModalOpened: false,
  currentCollectionTitle: '',
  errorMessage: {
    body: null,
    showCancelButton: true,
    shouldDisableBottomPadding: false,
    title: null,
  },
  isExportModalOpen: false,
  isContentBlocksModalOpen: false,
  isCMSModalOpen: {
    id: '',
    status: false,
  },
  activeContentBlock: null,
  isSearchLite: isHomePage(),
  snackbarInfo: {
    anchorOrigin: defaultAnchorOrigin,
    isOpen: false,
  },
  isComparisonActive: false,
  isCompareTableShown: false,
  comparisonItems: [],
  compareApiResults: [],
  compareApiKeys: undefined,
};

const commonSlice = createSlice({
  initialState,
  name: 'productSearchCommon',
  reducers: {
    addCartItemsInQueue(state, { payload = [] }) {
      const uniqItems = uniqBy(
        [...(state.cartItems || []), ...payload],
        (item) => item.entityId,
      );
      state.cartItems = uniqItems;
    },
    removeCartItemFromQueue(state, { payload = [] }) {
      state.cartItems = xorBy(
        [...(state.cartItems || [])],
        payload,
        'entityId',
      );
    },
    setCreatePaletteModalOpened(state, { payload }) {
      state.createPaletteModalOpened = payload;
    },
    setCurrentCollectionTitle(state, { payload }) {
      state.currentCollectionTitle = payload;
    },
    setErrorMessage(state, { payload }) {
      state.errorMessage = {
        body: payload.body || '',
        showCancelButton: payload?.showCancelButton,
        shouldDisableBottomPadding: payload?.shouldDisableBottomPadding,
        title: payload.title || '',
      };
    },
    setIsExportModalOpen(state, { payload }) {
      state.isExportModalOpen = payload;
    },
    setSnackbarInfo(state, { payload }: PayloadAction<SnackbarInfo>) {
      state.snackbarInfo = payload;
    },
    setIsComparisonActive(state, { payload }) {
      state.isComparisonActive = payload;
    },
    updateComparisonItems(state, { payload }) {
      const data = Array.isArray(payload) ? payload : [payload];
      state.comparisonItems = xorBy(
        [...(state.comparisonItems || [])],
        data,
        'entityId',
      );
    },
    resetComparisonFeature(state) {
      state.isComparisonActive = false;
      state.comparisonItems = [];
    },
    setIsCompareTableShown(state, { payload }) {
      state.isCompareTableShown = payload;
    },
    setCompareApiResults(state, { payload }) {
      state.compareApiResults = payload;
    },
    setCompareApiKeys(state, { payload }) {
      state.compareApiKeys = payload;
    },
    removeCompareApiItem(state, { payload }) {
      const currentList = [...(state.compareApiResults || [])];
      remove(currentList, (item: ProductSearchMaterial) => {
        return item.entityId === payload;
      });
      state.compareApiResults = currentList;
    },
    setContentBlocksModalOpen(state, { payload }) {
      state.isContentBlocksModalOpen = payload;
    },
    setCMSModalOpen(state, { payload }) {
      state.isCMSModalOpen = payload;
    },
    setActiveContentBlock(state, { payload }) {
      state.activeContentBlock = payload;
    },
  },
});

export const {
  addCartItemsInQueue,
  removeCartItemFromQueue,
  setCreatePaletteModalOpened,
  setCurrentCollectionTitle,
  setErrorMessage,
  setIsExportModalOpen,
  setSnackbarInfo,
  setIsComparisonActive,
  updateComparisonItems,
  resetComparisonFeature,
  setIsCompareTableShown,
  setCompareApiResults,
  setCompareApiKeys,
  removeCompareApiItem,
  setContentBlocksModalOpen,
  setCMSModalOpen,
  setActiveContentBlock,
} = commonSlice.actions;

export default commonSlice.reducer;
